import PageStructure, { PageContent, PageContentHeader, PageContentInner } from "@pages/PageStructure.component";
import { usePipelineNodes, useSources } from "@stores/data.store";
import { useCallback, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PipelineNode } from "@models/pipelineNode";
import SourceIcon from "@components/sources/SourceIcon.component";
import flatFileIcon from '@assets/images/connectors/flatfile.png';
import { timeAgo } from "@services/time.service";
import snowflakeIcon from '@assets/images/snowflake_logo.png';
import ObjectListHeader from "@components/common/ObjectListHeader.component";
import { summarizeNumber } from "@services/formatting.service";
import { DraftOnly, ProdOnly } from "@components/project/DraftModeRequired.component";
import { Source } from "@models/source";
import LoadingCard from "@components/card/LoadingCard.component";

const SourcesWrapperPage = () => {
    const sources = useSources();
    const nodes = usePipelineNodes();

    const allSourcesGrouped: Record<string, Source[]> = useMemo(() => {
        const data = sources.data || [];
        const allSourcesArray = [
            {
                id: 'snowflake',
                name: 'Snowflake',
                description: 'Connect directly to existing data in your Snowflake account',
                type: 'snowflake',
            },
            ...data
        ];
        
        return allSourcesArray.reduce((grouped: any, source) => {
            const type = source.type;
            if (!grouped[type]) {
                grouped[type] = [];
            }
            grouped[type].push(source);
            return grouped;
        }, {});
    }, [sources.dataUpdatedAt]);

    const nodesBySourceId = useMemo(() => {
        if (!nodes.data) {
            return {};
        }

        const result: Record<string, PipelineNode[]> = {};
        nodes.data.filter(n => n.node_type == 'SOURCE').forEach(n => {
            let sourceId = n.source_id;
            if (n.flat_file) {
                sourceId = 'flat_files';
            }
            if (!sourceId) {
                sourceId = 'snowflake';
            }
            if (!result[sourceId]) {
                result[sourceId] = [];
            }
            result[sourceId].push(n);
        });

        // Now sort them alphabetically
        Object.keys(result).forEach(k => {
            result[k] = result[k].sort((a, b) => a.name.localeCompare(b.name));
        });

        return result;
    }, [nodes.dataUpdatedAt, sources.dataUpdatedAt]);
    
    const [expandedSection, setExpandedSection] = useState('');
    const toggleExpanded = useCallback((section: string) => {
        setExpandedSection(expandedSection === section ? '' : section);
    }, [expandedSection]);

    const navigate = useNavigate();
    return <PageStructure>
        <PageContent>
            <PageContentHeader>
                <div className="d-flex center-vertically">
                    <div className="flex-1">
                        <h1 className="mb-0">Data Sources</h1>
                        <div className="text-muted font-13">Connect to your raw data sources to Pliable.</div>
                    </div>
                </div>
            </PageContentHeader>
            <PageContentInner hasHeader>
                <div className="p-4">
                    {Object.entries(allSourcesGrouped).map(([groupType, sourcesInGroup]) => {
                     const allNodesForSourceType = sourcesInGroup.flatMap(source => {
                        let sourceId = source.id as string;
                        if (source.type == 'flat_file') {
                            sourceId = 'flat_files';
                        }
                        return nodesBySourceId[sourceId] || [];
                    });
                    
                    

                    return <div key={groupType} >

                        

                    {sourcesInGroup.map(source => {
                        let sourceId = source.id as string;
                        if (source.type == 'flat_file') {
                            sourceId = 'flat_files';
                        }
            
                        const count = (nodesBySourceId[sourceId] || []).length;
                        
                        let headerIcon = null;
                        if (sourceId == 'flat_files') {
                            headerIcon = <SourceIcon compact image_url={flatFileIcon}/>;
                        } else if (sourceId == 'snowflake') {
                            headerIcon = <SourceIcon compact image_url={snowflakeIcon}/>;
                        } else {
                            headerIcon = <SourceIcon compact sourceId={sourceId}/>;
                        }

                        let actionButton = null;
                        if (!['snowflake', 'flat_files'].includes(sourceId)) {
                            actionButton = {
                                text: "Configure",
                                icon: "mdi-cog",
                                onClick: () => navigate(`/sources/${sourceId}`)
                            };
                        } else if (sourceId == 'flat_files') {
                            actionButton = {
                                text: "Add New",
                                icon: "mdi-upload",
                                onClick: () => navigate("/wizard/data-source?sourceType=file")
                            };
                        } else if (sourceId == 'snowflake') {
                            actionButton = {
                                text: "Add New",
                                icon: "mdi-table-plus",
                                onClick: () => navigate("/wizard/data-source?sourceType=snowflake")
                            };
                        }

                        return <div>
                            <ObjectListHeader
                                headerIcon={headerIcon}
                                title={source.name}
                                // subtitle={`${count} ${count == 1 ? 'table' : 'tables'}`}
                                actionButtonText={actionButton?.text}
                                actionButtonIcon={actionButton?.icon}
                                actionButtonOnClick={actionButton?.onClick}
                            />
                        </div>
                })}

                <div>
                            {nodes.isLoading && <div className="text-center my-5">
                                <LoadingCard minHeight={'100px'} />
                                </div>}
                            {(allNodesForSourceType).length == 0 && !nodes.isLoading &&<>
                                {groupType == 'snowflake' && <div className="empty-state mb-5">
                                    <div className="text-center">
                                        <h3>You haven't connected any Snowflake tables yet.</h3>
                                        <p>Pliable can pull data directly from any table in your Snowflake warehouse.</p>
                                        <DraftOnly>
                                            <Link className="btn btn-light" to={`/wizard/data-source?sourceType=snowflake`}>
                                                <i className="mdi mdi-table-plus"></i> Connect a Table
                                            </Link>
                                        </DraftOnly>
                                        <ProdOnly>
                                            <p>You are currently in <strong>Production Mode.</strong> To connect tables, please enter Develop Mode.</p>
                                        </ProdOnly>
                                        
                                    </div>
                                </div>}
                                {groupType == 'flat_files' && <div className="empty-state mb-5">
                                    <div className="text-center">
                                        <h3>You haven't uploaded any files yet.</h3>
                                        <p>Pliable can easily ingest any files that are readable by Excel (.xslx, .csv, etc). You can replace your add a new one any time you have more data, and the data will automatically flow into your data model.</p>
                                        <DraftOnly>
                                            <Link className="btn btn-light" to={`/wizard/data-source?sourceType=file`}>
                                                <i className="mdi mdi-upload"></i> Upload a File
                                            </Link>
                                        </DraftOnly>
                                        <ProdOnly>
                                            <p>You are currently in <strong>Production Mode.</strong> To upload files, please enter Develop Mode.</p>
                                        </ProdOnly>
                                        
                                    </div>
                                </div>}
                                {!['snowflake', 'flat_files'].includes(groupType) && <div className="empty-state mb-5">
                                    <div className="text-center">
                                        <h3><strong>{sourcesInGroup[0].name}</strong> isn't fully set up yet.</h3>
                                        <p>You may need to provide some additional configuration or run a sync.</p>
                                        
                                        <Link className="btn btn-light" to={`/sources/${sourcesInGroup[0].id}`}>
                                            <i className="mdi mdi-cog"></i> Configure
                                        </Link>
                                    </div>
                                </div>}
                            </>}
                            {(allNodesForSourceType).length > 0 && !nodes.isLoading &&
                            <table className="table table-bordered table-centered table-fixed mb-5">
                                <thead className="bg-light">
                                    <tr>
                                        <th style={{width: '50%'}}>Table Name</th>
                                        <th style={{width: '10%'}}>Records</th>
                                        <th style={{width: '20%'}}>Last Updated</th>
                                        <th style={{width: '20%'}}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(allNodesForSourceType).map(node => {

                                        return <tr key={node.id}>
                                            <td>
                                                <Link to={`/node/${node.id}`}>{node.label}</Link>
                                            </td>
                                            <td>{summarizeNumber(node.total_records || 0)}</td>
                                            <td>{node.last_build_completed ? timeAgo(node.last_build_completed) : 'Never'}</td>
                                            <td>
                                                <Link to={`/node/${node.id}`} className="btn btn-light btn-sm me-1"><i className="mdi mdi-table"></i> Data</Link>
                                                <Link to={`/node/${node.id}/config`} className="btn btn-light btn-sm"><i className="mdi mdi-cog"></i> Configure</Link>
                                            </td>
                                        </tr>
                                    })}
                                </tbody>
                            </table>}
                        </div>
                    
                </div> 
    })}
                </div>
            </PageContentInner>
        </PageContent>
    </PageStructure>
}

export default SourcesWrapperPage;
